import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, InfoWindow } from "@react-google-maps/api";
import { Loader2, Star, Navigation, MapPin, AlertTriangle } from "lucide-react";

// Yeni Places API ve Marker kütüphanesini ekliyoruz
const LIBRARIES = ["places", "marker"];

const containerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "0.75rem",
};

// Map ID (Varsa kendi ID'nizle değiştirin)
const MAP_ID = "c2d9ccff77d1edbaa87e83d3"; 

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapId: MAP_ID,
  // Harita stili (Karanlık Mod)
  styles: [
    { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
    {
      featureType: "administrative.locality",
      elementType: "labels.text.fill",
      stylers: [{ color: "#d59563" }],
    },
    {
      featureType: "poi",
      elementType: "labels.text.fill",
      stylers: [{ color: "#d59563" }],
    },
    {
      featureType: "poi.park",
      elementType: "geometry",
      stylers: [{ color: "#263c3f" }],
    },
    {
      featureType: "poi.park",
      elementType: "labels.text.fill",
      stylers: [{ color: "#6b9a76" }],
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#38414e" }],
    },
    {
      featureType: "road",
      elementType: "geometry.stroke",
      stylers: [{ color: "#212a37" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#9ca5b3" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [{ color: "#746855" }],
    },
    {
      featureType: "road.highway",
      elementType: "geometry.stroke",
      stylers: [{ color: "#1f2835" }],
    },
    {
      featureType: "road.highway",
      elementType: "labels.text.fill",
      stylers: [{ color: "#f3d19c" }],
    },
    {
      featureType: "transit",
      elementType: "geometry",
      stylers: [{ color: "#2f3948" }],
    },
    {
      featureType: "transit.station",
      elementType: "labels.text.fill",
      stylers: [{ color: "#d59563" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#17263c" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#515c6d" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#17263c" }],
    },
  ],
};

const NearbyFoodMap = ({ placeName, latitude, longitude }) => {
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(null);
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const markersRef = useRef([]);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const clearMarkers = () => {
    markersRef.current.forEach((marker) => (marker.map = null));
    markersRef.current = [];
  };

  useEffect(() => {
    if (isLoaded && map && latitude && longitude && window.google) {
      setLoadingSearch(true);
      setErrorMessage(null);
      clearMarkers();

      // Koordinatları float'a çeviriyoruz
      const location = { 
        lat: parseFloat(latitude), 
        lng: parseFloat(longitude) 
      };

      // Haritayı merkezle
      setCenter(location);
      map.panTo(location);
      map.setZoom(16);

      // 1. Ana Konum Marker'ı (Kırmızı Pin)
      const mainPin = new window.google.maps.marker.PinElement({
        background: "#EF4444", 
        borderColor: "#991B1B", 
        glyphColor: "white",
        scale: 1.2,
      });

      const mainMarker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: location,
        title: placeName || "Seçilen Konum",
        content: mainPin.element,
      });
      markersRef.current.push(mainMarker);

      // 2. Yakındaki Restoranları Ara (Places API New)
      const searchNearbyRestaurants = async () => {
        try {
          const request = {
            // Sadece "restoran" diye arıyoruz ama locationBias ile bu konuma zorluyoruz
            textQuery: "restaurant", 
            fields: ["displayName", "location", "rating", "userRatingCount", "formattedAddress", "photos"],
            maxResultCount: 10,
            locationBias: {
              center: location,
              radius: 1000 // 1km yarıçap
            },
          };

          const { places: searchResults } = await window.google.maps.places.Place.searchByText(request);

          if (searchResults && searchResults.length > 0) {
            const topPlaces = searchResults
              .filter((p) => p.rating > 0)
              .sort((a, b) => b.rating - a.rating)
              .slice(0, 3); // En iyi 3 tane

            setPlaces(topPlaces);

            topPlaces.forEach((place, index) => {
              if (place.location) {
                // Turuncu ve Numaralı Pinler
                const pin = new window.google.maps.marker.PinElement({
                  glyph: `${index + 1}`,
                  background: "#F97316",
                  borderColor: "#C2410C",
                  glyphColor: "white",
                  scale: 1.1,
                });

                const marker = new window.google.maps.marker.AdvancedMarkerElement({
                  map,
                  position: place.location,
                  title: place.displayName,
                  content: pin.element,
                });

                marker.addListener("click", () => {
                  setSelectedPlace({
                    ...place,
                    geometry: { location: place.location },
                    name: place.displayName,
                    user_ratings_total: place.userRatingCount,
                    vicinity: place.formattedAddress,
                  });
                });

                markersRef.current.push(marker);
              }
            });
          } else {
            console.warn("Yakında restoran bulunamadı.");
            setPlaces([]);
            setErrorMessage("Yakında kayıtlı restoran bulunamadı.");
          }
        } catch (error) {
          console.error("Places arama hatası:", error);
          setErrorMessage("Restoranlar yüklenirken bir hata oluştu.");
        } finally {
          setLoadingSearch(false);
        }
      };

      searchNearbyRestaurants();

    } else if (isLoaded && (!latitude || !longitude)) {
      setErrorMessage("Konum verisi eksik (Enlem/Boylam).");
      setLoadingSearch(false);
    }
  }, [isLoaded, map, latitude, longitude, placeName]);

  if (loadError) return <div className="text-red-400 p-4">Harita hatası: API Key kontrol edin.</div>;
  if (!isLoaded) return <div className="text-blue-400 p-4 flex"><Loader2 className="animate-spin mr-2"/> Harita yükleniyor...</div>;

  return (
    <div className="relative w-full h-full">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center || { lat: 39.9334, lng: 32.8597 }}
        zoom={15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      >
        {/* Markerlar useEffect içinde ekleniyor */}

        {/* InfoWindow */}
        {selectedPlace && (
          <InfoWindow
            position={selectedPlace.geometry.location}
            onCloseClick={() => setSelectedPlace(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -35) }}
          >
            <div className="p-0 w-64 text-gray-900">
              {selectedPlace.photos && selectedPlace.photos[0] && (
                <div className="h-32 w-full overflow-hidden rounded-t-lg">
                  <img 
                    src={selectedPlace.photos[0].getURI({ maxWidth: 400 })} 
                    alt={selectedPlace.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-3 bg-white rounded-b-lg">
                <h3 className="font-bold text-sm mb-1">{selectedPlace.name}</h3>
                
                <div className="flex items-center space-x-1 mb-2">
                  <span className="text-orange-500 font-bold text-sm bg-orange-100 px-1.5 rounded">
                    {selectedPlace.rating}
                  </span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className={`${
                          i < Math.round(selectedPlace.rating)
                            ? "fill-orange-500 text-orange-500"
                            : "fill-gray-200 text-gray-200"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    ({selectedPlace.user_ratings_total} yorum)
                  </span>
                </div>

                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {selectedPlace.vicinity}
                </p>

                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedPlace.name)}&query_place_id=${selectedPlace.id || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-2 px-3 rounded-md transition-colors"
                >
                  <Navigation className="w-3 h-3 mr-1.5" />
                  Yol Tarifi Al
                </a>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      
      {/* Bilgi Notu / Hata Mesajı */}
      <div className="absolute top-2 left-2 z-10">
        {errorMessage ? (
           <div className="bg-red-600/90 backdrop-blur-md text-white px-4 py-2 rounded-xl text-xs border border-red-500 shadow-2xl flex items-center gap-2">
             <AlertTriangle className="w-4 h-4"/>
             <span>{errorMessage}</span>
           </div>
        ) : (
          <div className="bg-gray-900/90 backdrop-blur-md text-white px-4 py-2 rounded-xl text-xs border border-gray-700 shadow-2xl flex items-center gap-3">
            {loadingSearch ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-orange-400"/> 
                <span className="font-medium">En lezzetli mekanlar aranıyor...</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium">
                  📍 {placeName} çevresindeki en iyi {places.length} lezzet noktası
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NearbyFoodMap;